import React, { useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Alert } from 'react-bootstrap';

import { useTranslation } from '../../../common/hooks/useTranslation';
import { ProjectSearch } from '../Projects/ProjectSearch';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateSalesInvoiceDto,
  GetPurchaserDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import ValidationAlert from '../../../app/components/ValidationAlert';

interface SalesInvoiceAddProjectEditorProps {
  show: boolean;
  handleAdd: (id: string) => void;
  handleClose: () => void;
  handleSave: () => void;
  salesInvoice: CreateUpdateSalesInvoiceDto;
  purchaser?: GetPurchaserDto;
  isLoading?: boolean;
  showSaveErrors: boolean;
  saveErrors: string[];
}

export const SalesInvoiceAddProjectEditor: React.FC<
  SalesInvoiceAddProjectEditorProps
> = props => {
  const { projectIds } = props.salesInvoice;
  const { t, tk } = useTranslation('fvsLng');
  const project = useTranslation('projectLng');
  const pok = usePokCore();

  const getData = useMemo(
    () =>
      (
        pageSize: number,
        pageNumber: number,
        orderBy?: string,
        orderDirection?: string,
        filterText?: string,
      ) =>
        pok.projects.getAllReadyToInvoiceByPortion(
          project.t,
          project.tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          props.purchaser?.client.id || '',
          props.purchaser?.budget.id || '',
          projectIds,
          true,
          true,
        ),
    [
      pok.projects,
      props.purchaser?.client.id,
      props.purchaser?.budget.id,
      projectIds,
      project.t,
      project.tk,
    ],
  );

  const getCount = useMemo(
    () => (filterText?: string) =>
      pok.projects.getAllReadyToInvoiceCount(
        filterText,
        props.purchaser?.client.id || '',
        props.purchaser?.budget.id || '',
        projectIds,
        true,
      ),
    [
      pok.projects,
      projectIds,
      props.purchaser?.budget.id,
      props.purchaser?.client.id,
    ],
  );

  return (
    <Modal
      show={props.show}
      onHide={props.handleClose}
      centered
      keyboard
      backdrop="static"
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t(tk.prLang.chooseProject)} {t(tk.finance.forContractor)}{' '}
          <b>{props.purchaser?.client.name}</b> {t(tk.finance.andBudget)}{' '}
          <b>{props.purchaser?.budget.name}</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProjectSearch
          getData={getData}
          getCount={getCount}
          onRowClick={props.handleAdd}
          showTotalDifferenceColumn
          totalDifferenceLabel={t(tk.finance.amountToInvoice)}
          disableGridHistory
          enableMultiselect
        />
        <Alert>
          {t(tk.prLang.selectProjectsPurchaseInvoiceAlert)}{' '}
          {t(tk.finance.toInvoice)}.
        </Alert>
        <ValidationAlert
          show={props.showSaveErrors}
          errors={props.saveErrors}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={props.handleClose}>
          {t(tk.button.close)}
        </Button>
        <Button onClick={props.handleSave} disabled={props.isLoading}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
